import {
    ComponentTypes,
    ITemplate,
    ITemplateComponent,
    ITemplateCheckboxComponent,
    ITemplateCounterpartyComponent,
    ITemplateDateInputComponent,
    ITemplateFileComponent,
    ITemplateHrPersonComponent,
    ITemplateInputComponent,
    ITemplateInputWithSelectComponent,
    ITemplateMoneyComponent,
    ITemplateMultilineComponent,
    ITemplateRadioComponent,
    ITemplatePdfPasswordComponent,
    ITemplateProcureComponent,
    ITemplateSaveAndTicketComponent,
    ITemplateSaveToExistingTicketComponent,
    ITemplateSelectComponent,
    ITemplateTableComponent,
    ITemplateTextComponent,
    ITemplateYandexComponent,
    IOrganization,
    IBusinessGroup,
    ITemplateBarcodeComponent,
    stringToWysiwygSettings
} from "entities/template";
import { AppLang } from "app";
import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { IValidationFunc } from "../../../lib/types/validation";
import { ExternalUpdateMode } from "../../../lib/types/share-state";
import { DocumentFormCheckbox } from "./document_form_checkbox/document_form_checkbox";
import { DocumentFormFile } from "./document_form_file/document_form_file";
import { DocumentFormInput } from "./document_form_input/document_form_input";
import { DocumentFormInputWithSelect } from "./document_form_input_with_select/document_form_input_with_select";
import { DocumentFormMoney } from "./document_form_money/document_form_money";
import { DocumentFormMultiline } from "./document_form_multiline/document_form_multiline";
import { DocumentFormRadio } from "./document_form_radio/document_form_radio";
import { DocumentFormDate } from "./document_form_date/document_form_date";
import { DocumentFormTable } from "./document_form_table/document_form_table";
import { DocumentFormText } from "./document_form_text/document_form_text";
import { DocumentFormSaveAndTicket } from "./document_form_save_and_ticket/document_form_save_and_ticket";
import {
    DocumentFormSaveToExistingTicket
} from "./document_form_save_to_existing_ticket/document_form_save_to_existing_ticket";
import { DocumentFormEmployee } from "./document_form_employee/document_form_employee";
import { DocumentFormYandex } from "./document_form_yandex/document_form_yandex";
import { DocumentFormLegalYandex } from "./document_form_legal_yandex/document_form_legal_yandex";
import { DocumentFormCounterparty } from "./document_form_counterparty/document_form_counterparty";
import { DocumentFormProcure } from "./document_form_procure/document_form_procure";
import { DocumentFormPdfPassword } from "./document_form_pdf_password/document_form_pdf_password";
import { DocumentExternalForm } from "./document_external_form/document_external_form";
import { IComponentTooltipSettings } from "./component_tooltip/component_tooltip";

interface IDocumentFormComponentProps {
    template: ITemplate;
    components: ITemplateComponent[];
    organizations: IOrganization[];
    labelShort: boolean;
    businessGroups: IBusinessGroup[];
    formRecord: Record<string, any>;
    readonly: boolean;
    onValidationRegister: (validationFunc: IValidationFunc, tag: string) => void;
    onValidationCheck: () => Promise<boolean>;
    component: ITemplateComponent;
    enLanguageEnabled: boolean;
    isExternalProtected: boolean;
    isExternalUser: boolean;
    onRecordChange: (partial: Record<string, any>) => void;
    externalUpdateRecord: Record<string, any>;
    externalUpdateMode: ExternalUpdateMode;
    triggerDocumentValidate: number;
    savingExternal?: boolean;
    onSaveExternalForm?: () => void;
}

export const DocumentFormComponent: React.FC<IDocumentFormComponentProps> = React.memo(({
                                                                                            component,
                                                                                            components,
                                                                                            organizations,
                                                                                            businessGroups,
                                                                                            template,
                                                                                            onValidationCheck,
                                                                                            formRecord,
                                                                                            readonly,
                                                                                            isExternalProtected,
                                                                                            isExternalUser,
                                                                                            labelShort,
                                                                                            triggerDocumentValidate,
                                                                                            externalUpdateRecord,
                                                                                            externalUpdateMode,
                                                                                            onValidationRegister,
                                                                                            enLanguageEnabled,
                                                                                            onRecordChange,
                                                                                            savingExternal,
                                                                                            onSaveExternalForm
                                                                                        }) => {
    const { i18n } = useTranslation();

    const label = useMemo(() => {
        const ruLabel = component.label;
        const enLabel = enLanguageEnabled && component.label_en ? component.label_en : component.label;
        return i18n.language === AppLang.RU ? ruLabel : enLabel;
    }, [component, enLanguageEnabled, i18n.language]);

    const tooltip = useMemo((): IComponentTooltipSettings => {
        const getTooltip = () => {
            const ruTooltip = component.tooltip;
            const enTooltip = enLanguageEnabled && component.tooltip_en ? component.tooltip_en : component.tooltip;
            return i18n.language === AppLang.RU ? ruTooltip : enTooltip;
        };

        const getWysiwygTooltip = () => {
            const ruWysiwygSettings = stringToWysiwygSettings(component.wysiwyg_tooltip);
            const enWysiwygSettings = stringToWysiwygSettings(component.wysiwyg_tooltip_en);
            const ruTooltip = ruWysiwygSettings.html;
            const enTooltip = enLanguageEnabled && enWysiwygSettings.html ? enWysiwygSettings.html : ruTooltip;
            return i18n.language === AppLang.RU ? ruTooltip : enTooltip;
        };

        return {
            tooltip: getTooltip(),
            wysiwygTooltip: getWysiwygTooltip(),
            useWysiwygTooltip: component.useWysiwygTooltip
        };

    }, [component, enLanguageEnabled, i18n.language]);


    return <>
        {component.type === ComponentTypes.checkbox &&
            <DocumentFormCheckbox
                tooltip={tooltip}
                readonly={readonly}
                component={component as ITemplateCheckboxComponent}
                externalUpdateRecord={externalUpdateRecord}
                label={label}
                onChange={(tag, value) => onRecordChange({ [tag]: value })}
            />}
        {component.type === ComponentTypes.dateInput &&
            <DocumentFormDate
                tooltip={tooltip}
                readonly={readonly}
                triggerValidate={triggerDocumentValidate}
                onValidationRegister={onValidationRegister}
                externalUpdateRecord={externalUpdateRecord}
                component={component as ITemplateDateInputComponent}
                label={label}
                labelShort={labelShort}
                onChange={onRecordChange}
            />}
        {component.type === ComponentTypes.file &&
            <DocumentFormFile
                tooltip={tooltip}
                triggerValidate={triggerDocumentValidate}
                externalUpdateRecord={externalUpdateRecord}
                onValidationRegister={onValidationRegister}
                component={component as ITemplateFileComponent}
                label={label}
                onChange={(tag, value) => onRecordChange({ [tag]: value })}
            />}
        {component.type === ComponentTypes.input &&
            <DocumentFormInput
                tooltip={tooltip}
                readonly={readonly}
                formRecord={formRecord}
                triggerValidate={triggerDocumentValidate}
                externalUpdateRecord={externalUpdateRecord}
                onValidationRegister={onValidationRegister}
                component={component as ITemplateInputComponent}
                label={label}
                labelShort={labelShort}
                onChange={(tag, value) => onRecordChange({ [tag]: value })}
            />}
        {component.type === ComponentTypes.inputWithSelect &&
            <DocumentFormInputWithSelect
                tooltip={tooltip}
                readonly={readonly}
                inputWithSelect={true}
                triggerValidate={triggerDocumentValidate}
                externalUpdateRecord={externalUpdateRecord}
                enLanguageEnabled={enLanguageEnabled}
                onValidationRegister={onValidationRegister}
                component={component as ITemplateInputWithSelectComponent}
                label={label}
                labelShort={labelShort}
                onChange={(tag, value) => onRecordChange({ [tag]: value })}
            />}
        {component.type === ComponentTypes.money &&
            <DocumentFormMoney
                tooltip={tooltip}
                externalUpdateRecord={externalUpdateRecord}
                triggerValidate={triggerDocumentValidate}
                readonly={readonly}
                enLanguageEnabled={enLanguageEnabled}
                formRecord={formRecord}
                onValidationRegister={onValidationRegister}
                component={component as ITemplateMoneyComponent}
                label={label}
                labelShort={labelShort}
                onChange={onRecordChange}
            />}
        {component.type === ComponentTypes.multiline &&
            <DocumentFormMultiline
                tooltip={tooltip}
                triggerValidate={triggerDocumentValidate}
                externalUpdateRecord={externalUpdateRecord}
                readonly={readonly}
                formRecord={formRecord}
                onValidationRegister={onValidationRegister}
                component={component as ITemplateMultilineComponent}
                label={label}
                labelShort={labelShort}
                onChange={(tag, value) => onRecordChange({ [tag]: value })}
            />}
        {component.type === ComponentTypes.radio &&
            <DocumentFormRadio
                tooltip={tooltip}
                triggerValidate={triggerDocumentValidate}
                readonly={readonly}
                enLanguageEnabled={enLanguageEnabled}
                onValidationRegister={onValidationRegister}
                externalUpdateRecord={externalUpdateRecord}
                component={component as ITemplateRadioComponent}
                label={label}
                labelShort={labelShort}
                onChange={(tag, value) => onRecordChange({ [tag]: value })}
            />}
        {component.type === ComponentTypes.select &&
            <DocumentFormInputWithSelect
                tooltip={tooltip}
                readonly={readonly}
                inputWithSelect={false}
                triggerValidate={triggerDocumentValidate}
                enLanguageEnabled={enLanguageEnabled}
                externalUpdateRecord={externalUpdateRecord}
                onValidationRegister={onValidationRegister}
                component={component as ITemplateSelectComponent}
                label={label}
                labelShort={labelShort}
                onChange={(tag, value) => onRecordChange({ [tag]: value })}
            />}
        {component.type === ComponentTypes.pdfPassword &&
            <DocumentFormPdfPassword
                tooltip={tooltip}
                readonly={readonly}
                externalUpdateRecord={externalUpdateRecord}
                triggerValidate={triggerDocumentValidate}
                onValidationRegister={onValidationRegister}
                component={component as ITemplatePdfPasswordComponent}
                label={label}
                labelShort={labelShort}
                onChange={(tag, value) => onRecordChange({ [tag]: value })}
            />}
        {component.type === ComponentTypes.table &&
            <DocumentFormTable
                enLanguageEnabled={enLanguageEnabled}
                readonly={readonly}
                tooltip={tooltip}
                externalUpdateRecord={externalUpdateRecord}
                onValidationRegister={onValidationRegister}
                component={component as ITemplateTableComponent}
                label={label}
                onChange={onRecordChange}
            />}
        {component.type === ComponentTypes.text &&
            <DocumentFormText
                tooltip={tooltip}
                component={component as ITemplateTextComponent}
                label={label}
            />}
        {component.type === ComponentTypes.hrPerson &&
            <DocumentFormEmployee
                tooltip={tooltip}
                externalUpdateMode={externalUpdateMode}
                triggerValidate={triggerDocumentValidate}
                readonly={readonly}
                externalUpdateRecord={externalUpdateRecord}
                onValidationRegister={onValidationRegister}
                component={component as ITemplateHrPersonComponent}
                label={label}
                onChange={onRecordChange}
            />}
        {component.type === ComponentTypes.yandex &&
            <DocumentFormYandex
                tooltip={tooltip}
                externalUpdateMode={externalUpdateMode}
                triggerValidate={triggerDocumentValidate}
                organizations={organizations}
                businessGroups={businessGroups}
                readonly={readonly}
                isExternalProtected={isExternalProtected}
                onValidationRegister={onValidationRegister}
                externalUpdateRecord={externalUpdateRecord}
                component={component as ITemplateYandexComponent}
                label={label}
                onChange={onRecordChange}
            />}
        {component.type === ComponentTypes.legalYandex &&
            <DocumentFormLegalYandex
                tooltip={tooltip}
                externalUpdateMode={externalUpdateMode}
                triggerValidate={triggerDocumentValidate}
                readonly={readonly}
                isExternalUser={isExternalUser}
                isExternalProtected={isExternalProtected}
                onValidationRegister={onValidationRegister}
                externalUpdateRecord={externalUpdateRecord}
                component={component as ITemplateYandexComponent}
                label={label}
                onChange={onRecordChange}
            />}
        {component.type === ComponentTypes.counterparty &&
            <DocumentFormCounterparty
                tooltip={tooltip}
                triggerValidate={triggerDocumentValidate}
                readonly={readonly}
                isExternalProtected={isExternalProtected}
                externalUpdateRecord={externalUpdateRecord}
                onValidationRegister={onValidationRegister}
                component={component as ITemplateCounterpartyComponent}
                label={label}
                onChange={onRecordChange}
            />}
        {component.type === ComponentTypes.barcode &&
            <DocumentFormMultiline
                tooltip={tooltip}
                triggerValidate={triggerDocumentValidate}
                readonly={readonly}
                formRecord={formRecord}
                externalUpdateRecord={externalUpdateRecord}
                onValidationRegister={onValidationRegister}
                component={component as ITemplateBarcodeComponent}
                label={label}
                labelShort={labelShort}
                onChange={(tag, value) => onRecordChange({ [tag]: value })}
            />}
        {component.type === ComponentTypes.iprocIntegration &&
            <DocumentFormProcure
                tooltip={tooltip}
                template={template}
                templateComponents={components}
                onValidationCheck={onValidationCheck}
                enLanguageEnabled={enLanguageEnabled}
                formRecord={formRecord}
                component={component as ITemplateProcureComponent}
                label={label}
            />}
        {component.type === ComponentTypes.saveAndTicket &&
            <DocumentFormSaveAndTicket {...{
                tooltip,
                component: component as ITemplateSaveAndTicketComponent,
                template,
                templateComponents: components,
                onValidationCheck,
                formRecord,
                enLanguageEnabled,
            }}/>}
        {component.type === ComponentTypes.saveToExistingTicket &&
            <DocumentFormSaveToExistingTicket
                tooltip={tooltip}
                label={label}
                isExternalProtected={isExternalProtected}
                externalUpdateRecord={externalUpdateRecord}
                onChange={(tag, value) => onRecordChange({ [tag]: value })}
                component={component as ITemplateSaveToExistingTicketComponent}
                enLanguageEnabled={enLanguageEnabled}
                template={template}
                templateComponents={components}
                onValidationCheck={onValidationCheck}
                formRecord={formRecord}
            />}
        {component.type === ComponentTypes.externalForm && onSaveExternalForm &&
            <DocumentExternalForm
                savingExternal={!!savingExternal}
                onSaveExternalForm={onSaveExternalForm}
            />}
    </>;
});
